import { Pool, TicketBody } from './types';
import api, { deleteData, getData, put } from 'services/api';

import { decamelizeKeys } from 'humps';
import { keepUpperCase } from 'utils/utils';

export const deleteTicket = (id: number) => {
	return deleteData(`/ticket_types/${id}`);
};

// Green-local create (mirrors updateTicket: decamelizeKeys + keepUpperCase, same /ticket_types endpoint). The
// batch-create endpoint takes an array. Kept in this feature's services rather than imported from the red-zone
// CreateTicket feature, which is slated for decommission — green↔red duplication is correct.
export const createTicket = (body: TicketBody) => {
	return api.post('/ticket_types', [
		decamelizeKeys(body, {
			process: keepUpperCase,
		}),
	]);
};

export const updateTicket = (body: TicketBody) => {
	return put(
		`/ticket_types/${body.ID}`,
		decamelizeKeys(body, {
			process: keepUpperCase,
		})
	);
};

export const getEventPools = (eventId: number): Promise<Pool[]> => {
	return getData(`/events/${eventId}/pools`);
};

// Raw pool-detail shape from GET /events/:id/pools/:poolId (snake_case, NOT camelized) — the same response
// PoolForm seeds from. Only the fields needed to re-PUT the pool are read.
interface RawPoolDetail {
	name: string;
	description?: string;
	total_amount: number;
	ticket_types?: { ID: number }[];
}

/**
 * Attach a ticket type to a shared pool via the **pool** endpoint.
 *
 * The batch ticket-create `POST /ticket_types` does not persist the `tickets_pool_id` FK (a backend gap), so
 * selecting a pool on the ticket form alone leaves the type unpooled. We set membership the way the Pools form
 * does — and that path is known to persist — by re-PUTting the pool's full member list with the new id appended
 * (deduped, so it is a no-op if the FK ever does land server-side). Best-effort: the ticket is already saved,
 * and the manager can still attach it from the Pools accordion if this call fails.
 */
export const addTicketTypeToPool = async (eventId: number, poolId: number, ticketTypeId: number): Promise<void> => {
	const pool: RawPoolDetail = await getData(`/events/${eventId}/pools/${poolId}`);
	const ticketTypeIds = Array.from(new Set([...(pool.ticket_types ?? []).map((tt) => tt.ID), ticketTypeId])).map(
		String
	);
	await put(`/events/${eventId}/pools/${poolId}`, {
		name: pool.name,
		description: pool.description ?? '',
		total_amount: pool.total_amount,
		ticket_type_ids: ticketTypeIds,
	});
};
